import React from 'react';
import { useParams } from 'next/navigation';
import { Category } from 'shared/types/product/Category';
import { useFormat } from 'helpers/hooks/useFormat';
import SidebarFooter from '../sidebar-footer';

interface CertNavProps {
  categories: Category[];
  activeCategory: Category;
  categoryId: string;
  isActiveECard: boolean;
  allProductText?: string;
  categoryOrder: string[];
  subCategoryOrders?: Record<string, string[]>;
}

const CertNav: React.FC<CertNavProps> = ({
  categories,
  activeCategory,
  allProductText,
  categoryId,
  isActiveECard,
  categoryOrder,
  subCategoryOrders = {},
}) => {
  const { locale } = useParams();
  const { formatMessage: formatMessagePadi } = useFormat({ name: 'padi' });
  const { formatMessage: formatMessageProduct } = useFormat({ name: 'product' });

  const mainCategory = categories.find((category) => category.slug === categoryId);
  const activeClass = 'font-bold text-padi-gray-darker';
  const nonActiveClass = 'hover:text-padi-gray-darker';
  const sortedMainCategory = mainCategory
    ? {
        ...mainCategory,
        subCategories: mainCategory.subCategories
          ?.sort((a, b) => {
            const indexA = categoryOrder.indexOf(a.key ?? '');
            const indexB = categoryOrder.indexOf(b.key ?? '');
            return indexA - indexB;
          })
          .map((category) => ({
            ...category,
            subCategories: category.subCategories?.sort((a, b) => {
              const order = subCategoryOrders[category.slug as keyof typeof subCategoryOrders];
              if (!order) return 0;
              const indexA = order.indexOf(a.slug ?? '');
              const indexB = order.indexOf(b.slug ?? '');
              return indexA - indexB;
            }),
          })),
      }
    : undefined;

  return (
    <div>
      <div className="flex flex-col gap-16 pb-30">
        <label className="text-lg font-semibold text-padi-gray-darkest">
          {formatMessageProduct({
            id: 'card.type',
            defaultMessage: 'Card Type',
          })}
        </label>
        <div className="flex gap-14 space-x-2">
          <a href={isActiveECard ? '' : `/${locale}/certification-card`}>
            <button
              className={
                isActiveECard
                  ? 'cursor-default rounded-lg bg-padi-gray-darkest px-31 py-14 text-xs font-medium text-white focus:outline-none'
                  : 'rounded-lg border-2 border-padi-gray bg-white px-31 py-14 text-xs font-medium text-padi-gray-darkest focus:outline-none'
              }
            >
              {formatMessageProduct({
                id: 'eCard',
                defaultMessage: 'eCard',
              })}
            </button>
          </a>
          <a href={isActiveECard ? `/${locale}/certification-card-physical` : ''}>
            <button
              className={
                isActiveECard
                  ? 'rounded-lg border-2 border-padi-gray bg-white px-31 py-14 text-xs font-medium text-padi-gray-darkest focus:outline-none'
                  : 'cursor-default rounded-lg bg-padi-gray-darkest px-31 py-14 text-xs font-medium text-white focus:outline-none'
              }
            >
              {formatMessagePadi({
                id: 'physical',
                defaultMessage: 'Physical',
              })}
            </button>
          </a>
        </div>
      </div>
      <h3 className="mb-10 text-base font-bold">
        <a
          href={`/${locale}${mainCategory?._url}`}
          className={activeCategory?.slug === mainCategory?.slug ? activeClass : nonActiveClass}
        >
          {allProductText}
        </a>
      </h3>
      <h3 className="sr-only">
        {formatMessageProduct({
          id: 'categories',
          defaultMessage: 'Categories',
        })}
      </h3>
      {sortedMainCategory?.subCategories?.map(
        (category, index) =>
          category?.slug &&
          categoryOrder.includes(category?.slug) && (
            <div key={`${category.name}-${index}`} className="pb-20">
              <h3 className="mb-10 text-base font-bold">
                <a
                  href={`/${locale}${category._url}`}
                  className={activeCategory?.slug === category.slug ? activeClass : nonActiveClass}
                >
                  {category.name}
                </a>
              </h3>
              <ul role="list" className="space-y-8 text-sm text-gray-900">
                {category?.subCategories &&
                  category?.subCategories.map((subCategory, index) => (
                    <li key={`${subCategory.name}-${index}`} className="pb-5">
                      <a
                        href={`/${locale}${subCategory._url}`}
                        className={activeCategory?.slug === subCategory.slug ? activeClass : nonActiveClass}
                      >
                        {subCategory.name}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          ),
      )}
      <SidebarFooter categories={categories} activeCategory={activeCategory} />
    </div>
  );
};

export default CertNav;
